/**
 * QuoteLinePrereqChips.tsx
 * Renders prerequisite chips for a single quote line item.
 *
 * Phase 5, Item 2 — Quote line prerequisite chips.
 *
 * Design (from mockup 01. ProjectQuote.png / screen-01-22m32s):
 *   - Each prerequisite renders as a Chip next to the test name.
 *   - If the prerequisite IS on the quote: neutral/success chip (theme.schedule.heatmapLow green).
 *   - If the prerequisite is NOT on the quote: warning chip (theme.schedule.conflictSoft orange)
 *     with a tooltip and an "Add" icon button.
 *   - If minSkillLevel is set, prefix with [L{n}] using SkillLevelChip convention.
 * No hex literals — all colors from theme.schedule.*.
 */

import React from 'react';
import { Box, Chip, Tooltip, IconButton } from '@mui/material';
import { AddCircleOutline } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import type { PrereqDetail } from '../../../services/quotePrerequisites';
import { formatPrereqChipLabel } from '../../../services/quotePrerequisites';

interface QuoteLinePrereqChipsProps {
  /** All prerequisites for this line item. */
  prerequisites: PrereqDetail[];
  /** Called when the user clicks "Add" on a missing prerequisite. */
  onAddPrerequisite?: (prereq: PrereqDetail) => void;
  /** Whether the quote is editable. If false, "Add" buttons are hidden. */
  readOnly?: boolean;
}

export const QuoteLinePrereqChips: React.FC<QuoteLinePrereqChipsProps> = ({
  prerequisites,
  onAddPrerequisite,
  readOnly = false,
}) => {
  const theme = useTheme();

  if (!prerequisites || prerequisites.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
      {prerequisites.map((prereq) => {
        const label = formatPrereqChipLabel(prereq);

        if (prereq.isOnQuote) {
          // Prerequisite is satisfied — show a neutral/success chip.
          return (
            <Tooltip
              key={prereq.requiredTestCatalogItemKey}
              title={`Prerequisite: ${prereq.requiredTestName} — already on this quote`}
              placement="top"
            >
              <Chip
                label={label}
                size="small"
                sx={{
                  backgroundColor: theme.schedule.heatmapLow,
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 20,
                  cursor: 'default',
                }}
              />
            </Tooltip>
          );
        }

        // Prerequisite is missing — show warning chip with optional Add button.
        return (
          <Box
            key={prereq.requiredTestCatalogItemKey}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}
          >
            <Tooltip
              title={`This test depends on "${prereq.requiredTestName}" — add it to the quote first.`}
              placement="top"
            >
              <Chip
                label={label}
                size="small"
                sx={{
                  backgroundColor: theme.schedule.conflictSoft,
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 20,
                  cursor: 'default',
                }}
              />
            </Tooltip>
            {!readOnly && onAddPrerequisite && (
              <Tooltip
                title={`Add "${prereq.requiredTestName}" to this quote (qty 1, default price)`}
                placement="top"
              >
                <IconButton
                  size="small"
                  onClick={() => onAddPrerequisite(prereq)}
                  sx={{
                    padding: '2px',
                    color: theme.schedule.conflictSoft,
                    '&:hover': { color: theme.schedule.conflictHard },
                  }}
                  aria-label={`Add prerequisite ${prereq.requiredTestName}`}
                >
                  <AddCircleOutline sx={{ fontSize: '0.9rem' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
